/**
 * Shared constants for AI return reason categorization functionality
 */

/**
 * The default question that starts the AI return reasons conversation
 */
export const AI_RETURN_REASONS_DEFAULT_QUESTION =
  "Why are you returning this item?";

/**
 * The default explanation subtext shown below the main question
 */
export const AI_RETURN_REASONS_DEFAULT_EXPLANATION_SUBTEXT =
  "Please explain in detail why you are returning this item.";

/**
 * The success message shown when AI categorizes the return reason
 */
export const AI_RETURN_REASONS_SUCCESS_MESSAGE =
  "Thank you for your feedback! Please continue below. If you want to change your reason, just type here.";

/**
 * isWrongProduct bills the merchant for the return label. Only a fulfillment
 * mismatch qualifies — not the shopper buying the wrong item (RET1-3249).
 */
export const AI_RETURN_REASONS_WRONG_PRODUCT_RULE = `Wrong Product: isWrongProduct is a fulfillment error, not a purchase mistake. Set isWrongProduct true ONLY if the customer clearly states that the item they received is not the item on their order (wrong SKU, color, style, size, shape, or product actually shipped, including units ordered as the same product that arrived different from one another). Wanting a different size or fit is not wrong product when they received the size they ordered: "I need a Large, not a Medium" means they received what they ordered.
Do NOT set isWrongProduct true when the customer ordered the wrong item, thought they ordered something else, meant to buy a different product, or wants a different item instead. Those are default returns. Example: "I thought I ordered an S-hook" while returning a different hook means they bought the wrong item — isWrongProduct must be false.
If it is unclear whether they received a different item than the order vs ordered the wrong thing, do not mark wrong product. Ask a clarifying question first if you have questions remaining; if you must categorize now, set isWrongProduct false.`;

export const AI_RETURN_REASONS_WRONG_PRODUCT_TOOL_DESCRIPTION =
  "True only if the merchant shipped a different item than the one on the order (different SKU, variant, color, design, or a product whose dimensions or shape do not match the listing). False if the customer received the size they ordered and wants another, ordered the wrong item, thought they ordered something else, wanted a different product, or the distinction is unclear.";

export const AI_RETURN_REASONS_FETCH_CATEGORIES_INSTRUCTIONS = `Categorize the customer's response into one of the categories.
Also set isDamaged and isWrongProduct.
isWrongProduct is true only if the customer received a different item than the one on their order (merchant shipped the wrong SKU/variant, or the item that arrived does not physically match the listing). It is false if they received the size they ordered and want another, ordered the wrong item, thought they ordered something else, or wanted a different product. When unclear, set isWrongProduct false.`;

/**
 * Which conversation the AI reasons step is running. Warranty conversations
 * collect a description of a product failure and emit no damaged or
 * wrong-product verdicts; everything else is a return conversation.
 */
export type AiReasonsConversationKind = "return" | "warranty";

export const AI_WARRANTY_REASONS_DEFAULT_QUESTION =
  "What issue are you experiencing with this item?";

export const AI_WARRANTY_REASONS_DEFAULT_EXPLANATION_SUBTEXT =
  "Please describe the problem, when it started, and how it happened.";

export function aiReasonsDefaultQuestion(
  kind: AiReasonsConversationKind | null | undefined,
): string {
  return kind === "warranty"
    ? AI_WARRANTY_REASONS_DEFAULT_QUESTION
    : AI_RETURN_REASONS_DEFAULT_QUESTION;
}

/** Question wording for agents that relay the step to a customer on the merchant's behalf. */
export function aiReasonsAgentFacingQuestion(
  kind: AiReasonsConversationKind | null | undefined,
): string {
  return kind === "warranty"
    ? "What problem is the customer having with this item? Describe what failed, when it started, and how it happened, in their own words."
    : "Why is the customer returning this item? Describe what happened in their own words.";
}

/** Warranty questions ask for more detail about the failure, so they get one more question by default. */
export function aiReasonsDefaultMaxQuestions(
  kind: AiReasonsConversationKind | null | undefined,
): number {
  return kind === "warranty" ? 3 : 2;
}

export const AI_WARRANTY_REASONS_FETCH_CATEGORIES_INSTRUCTIONS =
  "Categorize the customer's description into one of the categories. Choose the category that best names the failure: which part of the product failed and how.";
